/**
 * Daily AI rate limit — the I/O half.
 *
 * Reads the operator's config out of RTDB and delegates every decision to
 * `aiRateLimitConfig`, which is a leaf and therefore the only half of this the
 * test suite can reach. Same split, and the same reason, as the watchlock
 * module pair this mirrors: nothing here should contain a judgement.
 *
 * Read-only. The node is written by the schema-driven `SchemaConfigPanel`, same
 * as every other `configuration/*` node.
 */

import { ref as rtdbRef, get as rtdbGet } from 'firebase/database';

import { rtdb } from './firebase';
import {
  AI_RATE_LIMIT_CONFIG_PATH,
  DEFAULT_AI_RATE_LIMIT_CONFIG,
  resolveAiRateLimitConfig,
  type AiRateLimitConfig,
} from './aiRateLimitConfig';

/**
 * Read the live limiter config.
 *
 * Never throws. A failed read resolves to the shipped default — which is the
 * credit ledger, the limiter already running — matching the direction every
 * failure in the watchlock module takes. Here the stake is only what a readout
 * displays, but a screen that blanks on a blip is still worse than one showing
 * the defaults it would fall back to.
 */
export async function fetchAiRateLimitConfig(): Promise<AiRateLimitConfig> {
  try {
    const snap = await rtdbGet(rtdbRef(rtdb, `/${AI_RATE_LIMIT_CONFIG_PATH}`));
    return resolveAiRateLimitConfig(snap.exists() ? snap.val() : null);
  } catch {
    return DEFAULT_AI_RATE_LIMIT_CONFIG;
  }
}
